<template>
    <div>
        <div class="flex justify-between flex-col-reverse sm:flex-row sm:items-center">
            <div class="flex sm:items-center flex-col sm:flex-row">
                <table class="mx-2">
                    <tr>
                        <th scope="row" class="text-right text-sm whitespace-nowrap">{{ $t('commons.version') }} :</th>
                        <td class="px-3">
                            <app-select
                                :label="$t('commons.version')"
                                :value="preparation.id"
                                class=""
                                :show-label="false"
                                :disabled="!preparation.id"
                                @change="onVersionSelected($event)"
                            >
                                <option
                                    :value="version.id"
                                    v-for="(version, index) in preparation.versions"
                                    :key="version.id"
                                >
                                    {{ preparation.versions.length - index }} ({{ version.createdAt | humanizeDate }})
                                </option>
                            </app-select>
                        </td>
                    </tr>
                </table>

                <div class="m-2 sm:my-0">
                    <app-button @click="onNewVersion" :disabled="!preparation.id">
                        {{ $t('commons.newVersion') }}
                    </app-button>
                </div>
            </div>
        </div>
        <ValidationObserver v-slot="{ invalid, errors, dirty }" ref="observer">
            <form class="grid grid-cols-2">
                <app-fieldset class="col-span-2 mt-4" :label="$t('commons.infos')">
                    <app-input-text
                        :label="$t('commons.name')"
                        v-model="preparation.name"
                        class="col-span-2 md:col-span-1"
                        :required="true"
                        @blur="save()"
                        ref="nameInput"
                    />
                    <app-input-text
                        :label="$t('preparations.code')"
                        v-model="preparation.code"
                        class="col-span-2 md:col-span-1"
                        @blur="save()"
                    />
                    <app-bundle-picker
                        class="col-span-2"
                        v-model="preparation.bundle"
                        :options="bundles"
                        @input="onBundleChange"
                    ></app-bundle-picker>
                    <div>
                        <app-select
                            :label="$t('preparations.type')"
                            v-model="preparation.type"
                            class="col-span-2"
                            @change="save()"
                        >
                            <option value=""></option>
                            <optgroup :label="$t('preparations.groups.legalDocuments')">
                                <option value="kbis">{{ $t('preparations.types.kbis') }}</option>
                                <option value="status">{{ $t('preparations.types.status') }}</option>
                                <option value="idDocument">{{ $t('preparations.types.idDocument') }}</option>
                                <option value="signatory">{{ $t('preparations.types.signatory') }}</option>
                            </optgroup>
                            <optgroup :label="$t('preparations.groups.taxAndSocialDocuments')">
                                <option value="taxCertificate">
                                    {{ $t('preparations.types.taxCertificate') }}
                                </option>
                                <option value="URSAFFCertificate">
                                    {{ $t('preparations.types.URSAFFCertificate') }}
                                </option>
                                <option value="socialCertificate">
                                    {{ $t('preparations.types.socialCertificate') }}
                                </option>
                            </optgroup>
                            <optgroup :label="$t('preparations.groups.insurance')">
                                <option value="insuranceCertificate">
                                    {{ $t('preparations.types.insuranceCertificate') }}
                                </option>
                                <option value="tenYearsInsurance">
                                    {{ $t('preparations.types.tenYearsInsurance') }}
                                </option>
                            </optgroup>
                            <optgroup :label="$t('preparations.groups.financialDocuments')">
                                <option value="resultStates">{{ $t('preparations.types.resultStates') }}</option>
                                <option value="revenue">{{ $t('preparations.types.revenue') }}</option>
                                <option value="bankCertificate">Attestation bancaire</option>
                            </optgroup>
                            <optgroup :label="$t('preparations.groups.others')">
                                <option value="RIB">{{ $t('preparations.types.RIB') }}</option>
                                <option value="swornDeclaration">
                                    {{ $t('preparations.types.swornDeclaration') }}
                                </option>
                                <option value="professionalQualificationCertificate">
                                    {{ $t('preparations.types.professionalQualificationCertificate') }}
                                </option>
                                <option value="references">{{ $t('preparations.types.references') }}</option>
                                <option value="other">{{ $t('preparations.types.other') }}</option>
                            </optgroup>
                        </app-select>
                    </div>
                    <div v-if="preparation.type === 'subcontractingApproval'">
                        <app-select
                            :label="$t('amendments.subcontractor')"
                            v-model="preparation.subcontractorId"
                            @input="save()"
                        >
                            <option :value="null"></option>
                            <template v-for="subcontractor in preparation.bundle?.subcontractors">
                                <option :value="subcontractor.id">{{ subcontractor.name }}</option>
                            </template>
                        </app-select>
                    </div>
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('preparations.productionDates')">
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="preparation.emissionDueDate"
                        :label="$t('preparations.emissionDueDate')"
                        @input="save()"
                    ></app-date-input>
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="preparation.emissionDate"
                        :label="$t('preparations.emissionDate')"
                        @input="save()"
                    />
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="preparation.expireAt"
                        :label="$t('preparations.expirationDate')"
                        @input="save()"
                    />
                </app-fieldset>
                <app-save-on-leave :dirty="dirty" :saveFn="() => save(true)"></app-save-on-leave>
            </form>
        </ValidationObserver>
        <app-multi-picker
            ref="multiBundlePicker"
            class="col-span-2"
            variant="hidden"
            :show-label="false"
            v-model="bundlesToCopy"
            :options="bundles"
            :title="$t('preparations.copyByBundles')"
        >
            <template v-slot:option="{ option }">
                <app-bundle :bundle="option"></app-bundle>
            </template>
            <template v-slot:footer>
                <div class="p-4 flex justify-end">
                    <app-button
                        @click="copyToBundles"
                        :disabled="bundlesToCopy.length === 0"
                        variant="primary"
                        :label="$t('preparations.copyByBundleValidation')"
                    ></app-button>
                </div>
            </template>
        </app-multi-picker>
    </div>
</template>

<script>
import { getMapById, sanitize } from '@/services/sanitize.service';
import { createPreparation, getPreparation, updatePreparation } from '@/features/preparations/preparations.service';
import { combineLatest } from 'rxjs';
import { fetchSubcontractors, getBundles } from '@/features/bundles/bundles.service';
import { getPreparationVisas } from '@/features/preparations/preparationVisas.service';
import { isMobile } from '@/state/state';
import { getCompanies } from '@/features/companies/companies.service';
import AppSelect from '@/components/appSelect/AppSelect.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppFieldset from '@/components/appFieldset/AppFieldset.vue';
import AppInputText from '@/components/appInputText/AppInputText.vue';
import AppBundlePicker from '@/components/appBundlePicker.vue';
import AppDateInput from '@/components/appDateInput/AppDateInput.vue';
import AppSaveOnLeave from '@/components/AppSaveOnLeave.vue';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker.vue';
import AppBundle from '@/components/app-bundle/appBundle.vue';
import { newAdministrativeFile } from '@/features/administrativeFiles/AdministrativeFiles.service';
import AppCheckbox from '@/components/app-checkbox/AppCheckbox.vue';

export default {
    components: {
        AppCheckbox,
        AppBundle,
        AppMultiPicker,
        AppSaveOnLeave,
        AppDateInput,
        AppBundlePicker,
        AppInputText,
        AppFieldset,
        AppButton,
        AppSelect,
    },
    async created() {
        this.init();
        localStorage.setItem(
            'administrativeFiles.lastVisitedPreparationId.' + this.$route.params.projectId,
            this.$route.params.preparationId,
        );
    },
    watch: {
        $route: function (from, to) {
            if (from.name !== to.name || from.params.preparationId !== to.params.preparationId) {
                this.focusDone = false;
            }
            this.init();
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getPreparation(this.$route.params.projectId, this.$route.params.preparationId),
                    getPreparationVisas(this.$route.params.projectId, this.$route.params.preparationId),
                ]).subscribe(async ([bundles, companies, preparation, visas]) => {
                    const companyMap = getMapById(companies);
                    const subcontractors = preparation.bundleId
                        ? await fetchSubcontractors(this.$route.params.projectId, preparation.bundleId)
                        : [];
                    const mappedBundles = bundles.map((bundle) => ({
                        ...bundle,
                        company: companyMap[bundle.companyId],
                    }));
                    const bundleMap = getMapById(mappedBundles);
                    this.preparationVisas = visas.map((visa) => ({ ...visa, emitter: bundleMap[visa.emitterId] }));
                    this.preparation = {
                        ...preparation,
                        bundle: { subcontractors, ...bundleMap[preparation.bundleId] },
                    };
                    this.bundles = mappedBundles;
                    this.selectedVersion = this.$route.params.preparationId;
                    if (!this.focusDone && !isMobile && this.preparation.name === this.$t('preparations.newName')) {
                        this.$nextTick(() => {
                            this.$refs.nameInput.focus();
                            this.$refs.nameInput.select();
                        });
                        this.focusDone = true;
                    }
                }),
            ];
        },
        onBundleChange() {
            this.save();
        },
        async onVersionSelected(preparationId) {
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    preparationId,
                },
            });
        },
        async onNewVersion() {
            const preparationEntity = {
                groupId: this.preparation.groupId,
                name: this.preparation.name,
                code: this.preparation.code,
                emissionDate: null,
                emissionDueDate: this.preparation.emissionDueDate,
                bundleId: this.preparation.bundle ? this.preparation.bundle.id : null,
                phase: this.preparation.phase,
            };
            const result = await newAdministrativeFile(
                this.$route.params.projectId,
                this.bundles,
                preparationEntity,
                this.preparationVisas.map(({ emitterId }) => emitterId),
            );
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    preparationId: result.id,
                },
            });
        },
        sanitizePreparation(preparation, toOmit = []) {
            return {
                ...sanitize(preparation, [
                    'bundle',
                    'visas',
                    'documents',
                    'versions',
                    'conclusion',
                    'createdAt',
                    ...toOmit,
                ]),
                bundleId: preparation.bundle ? preparation.bundle.id : null,
                emissionDueDate: preparation.emissionDueDate || null,
            };
        },
        async save() {
            if (this.savingFlag) {
                return;
            } else {
                this.savingFlag = true;
            }
            try {
                const preparationEntity = this.sanitizePreparation(this.preparation);
                await updatePreparation(this.$route.params.projectId, {
                    id: this.preparation.id,
                    ...preparationEntity,
                });
                if (this.$refs.observer) {
                    this.$refs.observer.reset();
                }
            } finally {
                this.savingFlag = false;
            }
        },
        async createNew() {
            const result = await newAdministrativeFile(
                this.$route.params.projectId,
                this.bundles,
                { name: this.$t('preparations.newName') },
                [],
            );
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    preparationId: result.id,
                },
            });
        },
        async duplicate() {
            const result = await createPreparation(
                this.$route.params.projectId,
                {
                    ...this.sanitizePreparation(this.preparation, ['groupId']),
                    name: this.preparation.name + ' ' + this.$t('preparations.newCopySuffixName'),
                    phase: this.preparation.phase,
                    emissionDate: null,
                },
                this.preparationVisas.map((visa) => (visa.emitter ? visa.emitter.id : visa.emitterId)),
            );
            if (!result.errors) {
                this.$refs.observer.reset();
                await this.$router.push({
                    name: this.$route.name,
                    params: {
                        projectId: this.$route.params.projectId,
                        preparationId: result.id,
                    },
                });
            }
        },
        async copyToBundles() {
            this.$refs.multiBundlePicker.close();
            await Promise.all(
                this.bundlesToCopy.map((bundle) => {
                    return newAdministrativeFile(
                        this.$route.params.projectId,
                        this.bundles,
                        {
                            name: this.preparation.name,
                            code: this.preparation.code,
                            phase: this.preparation.phase,
                            bundleId: bundle.id,
                            emissionDueDate: this.preparation.emissionDueDate,
                        },
                        this.preparationVisas.map((visa) => (visa.emitter ? visa.emitter.id : visa.emitterId)),
                    );
                }),
            );
            this.bundlesToCopy = [];
        },
        async copyByBundles() {
            this.$refs.multiBundlePicker.open();
        },
    },
    data() {
        return {
            focusDone: false,
            subscriptions: [],
            savingFlag: false,
            bundles: [],
            bundlesToCopy: [],
            preparationVisas: [],
            preparation: {
                name: this.$t('preparations.newName'),
                bundle: null,
                documents: [],
                versions: [],
                deliveryDuration: 0,
            },
            selectedVersion: null,
            project: {},
        };
    },
};
</script>
